import React, { useCallback, useEffect, useState } from 'react';
import {
  DeliveryService,
  RecordingService,
  ProblemReportDTO,
  ProblemTypesDTO,
  UpdateProblemReportDTO,
} from '../api/generated';
import { useSnackbar } from './SnackbarProvider';

export interface FeedbackSnackbarState {
  isVisible: boolean;
  isLoading: boolean;
  isExpanded: boolean;
  problemTypes: ProblemTypesDTO;
  currentFeedback?: ProblemReportDTO;
  hasSubmittedFeedback: boolean;
}

export interface FeedbackSnackbarActions {
  handleReportProblem: () => void;
  handleClose: () => void;
  handleCheckboxChange: (field: keyof ProblemTypesDTO) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputChange: (field: keyof ProblemTypesDTO) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => Promise<void>;
}

export interface UseFeedbackSnackbarProps {
  contentType: 'delivery' | 'recording';
  jobsiteId: string;
  contentId: string;
  autoShowDelay?: number;
}

export interface UseFeedbackSnackbarReturn extends FeedbackSnackbarActions, FeedbackSnackbarState {}

const initialProblemTypes: ProblemTypesDTO = {
  image: false,
  prediction: false,
  assignment: false,
  other: false,
  comment: '',
};

export const useFeedbackSnackbar = ({
  contentType,
  jobsiteId,
  contentId,
  autoShowDelay = 2000,
}: UseFeedbackSnackbarProps): UseFeedbackSnackbarReturn => {
  const [state, setState] = useState<FeedbackSnackbarState>({
    isVisible: false,
    isLoading: false,
    isExpanded: false,
    problemTypes: initialProblemTypes,
    hasSubmittedFeedback: false,
  });
  const { showSnackbar } = useSnackbar();

  const resetFeedbackState = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isExpanded: false,
      problemTypes: initialProblemTypes,
    }));
  }, []);

  // Reset state when switching between different entities
  useEffect(() => {
    setState({
      isVisible: false,
      isLoading: false,
      isExpanded: false,
      problemTypes: initialProblemTypes,
      hasSubmittedFeedback: false,
    });
  }, [contentType, jobsiteId, contentId]);

  // Reset expanded state when snackbar becomes invisible
  useEffect(() => {
    if (!state.isVisible) {
      resetFeedbackState();
    }
  }, [state.isVisible, resetFeedbackState]);

  // Auto-show feedback and initialize after delay
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!state.hasSubmittedFeedback) {
        // Initialize feedback report when showing
        try {
          setState((prev) => ({ ...prev, isLoading: true }));

          let response: ProblemReportDTO;
          if (contentType === 'delivery') {
            const result = await DeliveryService.deliveryControllerCreateProblemReport({
              path: { jobsiteId, deliveryId: contentId },
            });
            if (!result.data) {
              throw new Error('No data received from create problem report API');
            }
            response = result.data;
          } else {
            const result = await RecordingService.recordingControllerCreateProblemReport({
              path: { jobsiteId, recordingId: contentId },
            });
            if (!result.data) {
              throw new Error('No data received from create problem report API');
            }
            response = result.data;
          }

          setState((prev) => ({
            ...prev,
            currentFeedback: response,
            hasSubmittedFeedback: Boolean(response.problem),
            problemTypes: response.problem || initialProblemTypes, // Populate with existing feedback or defaults
            isVisible: true, // Always show snackbar
            isLoading: false,
          }));
        } catch (error) {
          console.error('Failed to initialize feedback:', error);
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      }
    }, autoShowDelay);

    return () => clearTimeout(timer);
  }, [autoShowDelay, state.hasSubmittedFeedback, contentType, jobsiteId, contentId]);

  const handleReportProblem = useCallback(() => {
    setState((prev) => ({ ...prev, isExpanded: true }));
  }, []);

  const handleClose = useCallback(() => {
    resetFeedbackState();
    setState((prev) => ({ ...prev, isVisible: false }));
  }, [resetFeedbackState]);

  const handleCheckboxChange = useCallback((field: keyof ProblemTypesDTO) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((prev) => ({
        ...prev,
        problemTypes: {
          ...prev.problemTypes,
          [field]: event.target.checked,
        },
      }));
    };
  }, []);

  const handleInputChange = useCallback((field: keyof ProblemTypesDTO) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const v = event.target.value || '';
      setState((prev) => ({
        ...prev,
        problemTypes: {
          ...prev.problemTypes,
          [field]: v,
        },
      }));
    };
  }, []);

  const onSubmit = useCallback(async () => {
    if (!state.currentFeedback) return;

    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      const updateData: UpdateProblemReportDTO = {
        problem: state.problemTypes,
      };

      let response: ProblemReportDTO;
      if (contentType === 'delivery') {
        const result = await DeliveryService.deliveryControllerUpdateProblemReport({
          path: { jobsiteId, deliveryId: contentId },
          body: updateData,
        });
        if (!result.data) {
          throw new Error('No data received from update problem report API');
        }
        response = result.data;
      } else {
        const result = await RecordingService.recordingControllerUpdateProblemReport({
          path: { jobsiteId, recordingId: contentId },
          body: updateData,
        });
        if (!result.data) {
          throw new Error('No data received from update problem report API');
        }
        response = result.data;
      }

      setState((prev) => ({
        ...prev,
        currentFeedback: response,
        hasSubmittedFeedback: true,
        isVisible: false,
        isLoading: false,
      }));

      showSnackbar('feedback.success', 'success');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setState((prev) => ({ ...prev, isLoading: false }));
      showSnackbar('feedback.error', 'error');
    }
  }, [state.currentFeedback, state.problemTypes, contentType, jobsiteId, contentId, showSnackbar]);

  return {
    ...state,
    handleReportProblem,
    handleClose,
    handleCheckboxChange,
    handleInputChange,
    onSubmit,
  };
};
